import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Chip, Stack, Menu, MenuItem, Typography, CircularProgress } from '../../../ui/core';
import RideIcon from '../../../ui/icons/RideIcon';
import DriveIcon from '../../../ui/icons/DriveIcon';
import { editDriver } from '../../../redux/Drivers/actions';

const formatDriverType = ({ isCustomDriver }) => {
  return isCustomDriver ? 'On-call driver' : 'Going anyway driver';
};

// A pill, not the theme's 4px rounded rect. `MuiChip` is squared off globally, but this
// chip sits beside `TintedStatusPill` on the Driver tab's status card — same size, same
// row, one of them rounded — so it takes the pill radius locally rather than the theme
// changing shape for every chip in the app.
// `maxWidth: 100%` + a truncating label so the widest type ("Going anyway driver") does
// not push the pill past the right edge of a card that clips — the Driver tab's status
// card is ~270px wide on a phone, and 96px of that is the row's label column.
const PILL_SX = {
  borderRadius: '16px',
  maxWidth: '100%',
  '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
};

const TypeChip = ({ value, loading }) => {
  const label = formatDriverType({ isCustomDriver: value });

  if (value) {
    return (
      <Chip
        sx={PILL_SX}
        icon={loading ? <CircularProgress size={14} /> : <DriveIcon sx={{ fontSize: 14 }} />}
        label={label}
      />
    );
  }

  return (
    <Chip
      sx={PILL_SX}
      icon={loading ? <CircularProgress size={14} /> : <RideIcon sx={{ fontSize: 14 }} />}
      label={label}
    />
  );
};

const DriverTypeField = ({ driver }) => {
  const { driverType } = driver;
  const isProfessional = driverType === 'professional';
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onSelectType = async (v) => {
    handleClose();
    if (v !== isProfessional) {
      setIsSubmitting(true);
      await editDriver(driver, { isCustomDriver: v, driverType: v ? 'professional' : 'casual' })(dispatch);
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ minWidth: 0 }}>
      <Button
        id="driver-type-button"
        aria-controls={open ? 'driver-type-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        disabled={isSubmitting}
        sx={{ minWidth: 0, maxWidth: '100%' }}
      >
        <TypeChip value={isProfessional} loading={isSubmitting} />
      </Button>
      <Menu
        id="driver-type-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'driver-type-button',
        }}
      >
        <MenuItem disabled divider>
          <Typography>Change driver type</Typography>
        </MenuItem>
        <MenuItem value={true} onClick={() => onSelectType(true)}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DriveIcon sx={{ fontSize: 14 }} color="text.primary" />
            <Typography>{formatDriverType({ isCustomDriver: true })}</Typography>
          </Stack>
        </MenuItem>
        <MenuItem value={false} onClick={() => onSelectType(false)}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RideIcon sx={{ fontSize: 14 }} color="text.primary" />
            <Typography>{formatDriverType({ isCustomDriver: false })}</Typography>
          </Stack>
        </MenuItem>
      </Menu>
    </div>
  );
};

export default DriverTypeField;
